import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const BoxTracking = () => {
  const { showSuccess, showError } = useToast();
  const [entries, setEntries] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [custName, setCustName] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [sendingBox, setSendingBox] = useState('');
  const [receiveBox, setReceiveBox] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [boxRes, custRes] = await Promise.all([
        axiosClient.get('/boxes'),
        axiosClient.get('/accounts?type=CUSTOMER')
      ]);

      if (boxRes.success) {
        setEntries(boxRes.data || []);
        setSummaries(boxRes.summary || []);
      }
      if (custRes.success) {
        setCustomers(custRes.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!custName) {
      showError('Please select or enter customer name.');
      return;
    }

    setSaving(true);
    try {
      await axiosClient.post('/boxes', {
        date,
        cust_name: custName,
        vehicle_no: vehicleNo,
        sending_box: parseInt(sendingBox, 10) || 0,
        receive_box: parseInt(receiveBox, 10) || 0
      });

      showSuccess(`Box tracking record saved successfully for ${custName}!`);
      setSendingBox('');
      setReceiveBox('');
      setVehicleNo('');
      fetchData();
    } catch (err) {
      showError(err.message || 'Error saving box entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this box record?')) {
      try {
        await axiosClient.delete(`/boxes/${id}`);
        showSuccess('Box record deleted successfully.');
        fetchData();
      } catch (err) {
        showError(err.message || 'Error deleting entry.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-box-seam-fill text-primary me-2"></i> Box / Crate Tracking
          </h3>
          <p className="text-muted small mb-0">Track sending and receiving of fish storage crates/boxes per customer</p>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Form & Balances */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2 fw-semibold">New Box / Crate Record</div>
            <div className="card-body p-3">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small text-muted">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small text-muted">Customer Name *</label>
                  <input
                    type="text"
                    list="customerList"
                    className="form-control"
                    placeholder="Search or enter customer name"
                    value={custName}
                    onChange={(e) => setCustName(e.target.value)}
                    required
                  />
                  <datalist id="customerList">
                    {customers.map((c) => (
                      <option key={c.Id} value={c.AccountName} />
                    ))}
                  </datalist>
                </div>

                <div className="mb-3">
                  <label className="form-label small text-muted">Vehicle No</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. MH-06-K-9000"
                    value={vehicleNo}
                    onChange={(e) => setVehicleNo(e.target.value)}
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted">Sending Boxes</label>
                    <input
                      type="number"
                      className="form-control text-center fw-bold text-primary"
                      placeholder="0"
                      value={sendingBox}
                      onChange={(e) => setSendingBox(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Received Boxes</label>
                    <input
                      type="number"
                      className="form-control text-center fw-bold text-success"
                      placeholder="0"
                      value={receiveBox}
                      onChange={(e) => setReceiveBox(e.target.value)}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-ocean w-100" disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-save me-1"></i>}
                  Save Box Record
                </button>
              </form>
            </div>
          </div>

          {/* Customer Box Balances Table */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-2 fw-semibold">Customer Crate Balances</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Customer</th>
                      <th className="text-center">Sent</th>
                      <th className="text-center">Received</th>
                      <th className="text-end">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaries.map((s, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold">{s.cust_name}</td>
                        <td className="text-center text-primary">{s.totalSending}</td>
                        <td className="text-center text-success">{s.totalReceive}</td>
                        <td className={`text-end fw-bold ${s.totalBalance > 0 ? 'text-danger' : 'text-muted'}`}>
                          {s.totalBalance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right Log Table */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3">
              <h5 className="fw-bold mb-0 text-dark">Recent Box Movements Log</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Vehicle</th>
                      <th className="text-center">Sent</th>
                      <th className="text-center">Received</th>
                      <th className="text-end">Balance</th>
                      <th className="text-center" style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                        </td>
                      </tr>
                    ) : entries.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted">No box movements recorded.</td>
                      </tr>
                    ) : (
                      entries.map((e) => (
                        <tr key={e.id}>
                          <td>{formatDate(e.date)}</td>
                          <td className="fw-bold text-dark">{e.cust_name}</td>
                          <td>{e.vehicle_no || '-'}</td>
                          <td className="text-center text-primary fw-semibold">{e.sending_box}</td>
                          <td className="text-center text-success fw-semibold">{e.receive_box}</td>
                          <td className="text-end fw-bold text-danger">{e.balance_box}</td>
                          <td className="text-center">
                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDelete(e.id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxTracking;
